import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { History, Loader2, BellRing } from "lucide-react";
import { getScans, getAlertHistory } from "@/lib/api";
import { timeAgo } from "@/lib/format";

export default function ScanHistory({ trigger }) {
  const [items, setItems] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const load = () => {
    getScans().then((d) => setItems(d.items || []));
    getAlertHistory().then((d) => setAlerts(d.items || [])).catch(() => setAlerts([]));
  };

  return (
    <Sheet onOpenChange={(o) => o && load()}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="bg-white border-l border-[#E2E8F0] text-slate-800 w-full sm:max-w-md overflow-y-auto" data-testid="scan-history-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-slate-900 flex items-center gap-2"><History size={16} className="text-[#0070BA]" /> Activity</SheetTitle>
        </SheetHeader>

        {alerts.length > 0 && (
          <div className="mt-5">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5 mb-2"><BellRing size={13} className="text-[#00873C]" /> Alert History</div>
            <div className="space-y-2">
              {alerts.map((a, i) => (
                <div key={a.at || i} className="border border-[#E2E8F0] bg-[#F0FDF4] rounded-[4px] p-3" data-testid={`alert-history-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[12px] font-semibold text-slate-800">{a.title}</span>
                    <span className="font-mono text-[10px] text-slate-400">{timeAgo(a.at)}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">{a.body}</div>
                  <div className="font-mono text-[9px] text-slate-400 mt-1">delivered to {a.sent_to} device(s)</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-6 mb-2">Scan History</div>
        {!items ? (
          <div className="mt-2 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="mt-2 text-[11px] text-slate-400 text-center">No scans logged yet.</div>
        ) : (
          <div className="space-y-2">
            {items.map((s, i) => (
              <div key={s.finished_at || i} className="border border-[#E2E8F0] bg-[#F8FAFC] rounded-[4px] p-3" data-testid={`scan-history-row-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wide">{s.scope}</span>
                  <span className="font-mono text-[10px] text-slate-400">{timeAgo(s.finished_at)}</span>
                </div>
                <div className="flex items-center gap-3 mt-2 font-mono text-[10px] text-slate-500 tabular-nums">
                  <span>{s.scanned}/{s.total} analysed</span>
                  <span style={{ color: s.matches ? "#00873C" : "#94A3B8" }}>{s.matches} matched</span>
                  <span>{s.duration_sec}s</span>
                </div>
                {s.matched_tickers?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {s.matched_tickers.map((t) => (
                      <span key={t} className="font-mono text-[9px] px-1.5 py-0.5 rounded-[3px] bg-white border border-[#E2E8F0] text-slate-600">{t.replace(".NS", "")}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
